import { useEffect, useState } from 'react';
import { HourglassMedium } from '@phosphor-icons/react';

import Modal from '@/components/molecules/Modal/Modal';
import { useInvoiceV2Tracking } from '../../hooks/useInvoiceV2Tracking';
import type { CsdSealActivation } from '../../services/csdSeal.service';
import { useSealStatus } from '../hooks/useSealStatus';
import { InvoiceButton } from './InvoiceButton';
import { SealSupportButton } from './SealSupportButton';

const SEEN_KEY = 'heru.invoicing.sealActivationNotice';

/** Una vez por sesión y por episodio: un rechazo nuevo vuelve a avisar. */
const episodeOf = (activation: CsdSealActivation) => `${activation.reason ?? ''}:${activation.since ?? ''}`;

const alreadySeen = (episode: string): boolean => {
  try {
    return sessionStorage.getItem(SEEN_KEY) === episode;
  } catch {
    return false;
  }
};

const markSeen = (episode: string) => {
  try {
    sessionStorage.setItem(SEEN_KEY, episode);
  } catch {
    // Sin sessionStorage el aviso puede repetirse en la sesión; no rompe nada.
  }
};

const hoursSince = (iso: string | null): number | null => {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  return Number.isFinite(ms) ? Math.max(0, Math.round(ms / 36e5)) : null;
};

const COPY = {
  new_certificate: {
    title: 'Tus sellos se están activando',
    body: 'El SAT tarda hasta 72 horas en activar los sellos nuevos. Si timbras antes, puede rechazar la factura.',
  },
  lco_rejection: {
    title: 'El SAT todavía no activa tus sellos',
    body: 'Tu última factura no se emitió porque tu RFC aún no aparece en la lista del SAT de quienes pueden facturar. Con sellos nuevos tarda hasta 72 horas; si ya pasó más tiempo, escríbenos y lo revisamos.',
  },
} as const;

/**
 * El aviso de que el SAT todavía no acepta el sello, al entrar a la facturación.
 *
 * Sale antes del error en vez de después: el 2-oct-2026 una persona lo intentó 4
 * veces en 11 minutos y recibió el texto crudo del PAC cada vez. Es un aviso y no
 * un candado: la señal de «certificado nuevo» es estadística (activation.go en el
 * fiscal-engine) y un intento que pasa no le cuesta nada a nadie.
 */
export const SealActivationNotice = () => {
  const { pendingActivation } = useSealStatus();
  const { trackCsdActivationPendingShown } = useInvoiceV2Tracking();
  const [openEpisode, setOpenEpisode] = useState<string | null>(null);

  useEffect(() => {
    if (!pendingActivation) return;
    const episode = episodeOf(pendingActivation);
    if (alreadySeen(episode)) return;
    markSeen(episode);
    setOpenEpisode(episode);
    trackCsdActivationPendingShown({
      reason: pendingActivation.reason,
      hoursSince: hoursSince(pendingActivation.since),
    });
  }, [pendingActivation, trackCsdActivationPendingShown]);

  if (!pendingActivation || openEpisode !== episodeOf(pendingActivation)) return null;
  const copy = COPY[pendingActivation.reason ?? 'new_certificate'];

  return (
    <Modal open onClose={() => setOpenEpisode(null)} sx='!max-w-[420px]'>
      <div className='flex flex-col gap-4 pt-1'>
        <span className='bg-h-warning-95 flex size-11 items-center justify-center rounded-full'>
          <HourglassMedium size={22} weight='fill' className='text-h-warning-30' />
        </span>
        <div className='flex flex-col gap-1.5 pr-8'>
          <h2 className='text-h-text-heading text-lg leading-snug font-bold'>{copy.title}</h2>
          <p className='text-h-text-medium text-[14px] leading-relaxed'>{copy.body}</p>
        </div>
        <div className='flex flex-col gap-2'>
          <SealSupportButton
            variant='secondary'
            context={
              pendingActivation.reason === 'lco_rejection'
                ? 'El SAT rechaza mis facturas porque mi RFC no aparece en su lista de quienes pueden facturar.'
                : 'Mis sellos son nuevos y quiero saber cuándo puedo timbrar.'
            }
          />
          <InvoiceButton onClick={() => setOpenEpisode(null)}>Entendido</InvoiceButton>
        </div>
      </div>
    </Modal>
  );
};
